import React, { useState, useCallback } from 'react';
import './FuturisticFileExplorer.scss';

// Basic SVG Icons (inline for simplicity, recommend using an icon library or SVG files)
const FolderIconOpen = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
    <path d="M6.17 6L4 8v10h16V8h-5.17l-1.42-2H6.17zm-2-2h8l2 2H20a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2z" />
  </svg>
);
const FolderIconClosed = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
    <path d="M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z" />
  </svg>
);
const FileIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em">
    <path d="M13 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V9l-7-7zm4 18H7V4h5v5h5v11z" />
  </svg>
);


export interface FileSystemEntry { /* ... (interface remains the same) ... */
  id: string;
  name: string;
  path: string;
  type: 'file' | 'folder';
  children?: FileSystemEntry[];
  isOpen?: boolean;
}

// ... (FileNode and FuturisticFileExplorer component logic remains the same) ...
interface FuturisticFileExplorerProps {
  fileTree: FileSystemEntry[];
  onFileSelect: (path: string) => void;
  activeFile: string | null;
}

const FileNode: React.FC<{
  node: FileSystemEntry;
  onFileSelect: (path: string) => void;
  onToggleFolder: (path: string) => void;
  activeFile: string | null;
  level: number;
}> = ({ node, onFileSelect, onToggleFolder, activeFile, level }) => {
  const handleSelect = () => {
    if (node.type === 'file') {
      onFileSelect(node.path);
    } else {
      onToggleFolder(node.path);
    }
  };

  const indent = level * 20;

  return (
    <div
      className={`file-entry type-${node.type} ${activeFile === node.path ? 'active' : ''} ${node.isOpen ? 'open' : ''}`}
      style={{ paddingLeft: `${indent + 10}px` }} // Adjusted base padding slightly
      onClick={handleSelect}
      onKeyPress={(e) => e.key === 'Enter' && handleSelect()}
      role="button"
      tabIndex={0}
    >
      <span className="icon">
        {node.type === 'folder' ? (
          node.isOpen ? <FolderIconOpen /> : <FolderIconClosed />
        ) : (
          <FileIcon />
        )}
      </span>
      <span className="name">{node.name}</span>
    </div>
  );
};

const FuturisticFileExplorer: React.FC<FuturisticFileExplorerProps> = ({ /* ... props ... */
  fileTree: initialFileTree,
  onFileSelect,
  activeFile,
}) => {
  const [internalFileTree, setInternalFileTree] = useState<FileSystemEntry[]>([]);

  React.useEffect(() => {
    const enrichTree = (nodes: FileSystemEntry[], existingNodes?: FileSystemEntry[]): FileSystemEntry[] => {
      return nodes.map(node => {
        const existingNode = existingNodes?.find(n => n.id === node.id);
        const children = node.children ? enrichTree(node.children, existingNode?.children) : undefined;
        // For folders, default isOpen to false if not previously set.
        // For files, isOpen is undefined.
        // Let's make root folders open by default for better initial view.
        const defaultOpen = node.type === 'folder' && (node.path.split('/').length -2) === 0; // Open root folders

        return {
          ...node,
          isOpen: existingNode?.isOpen ?? (node.type === 'folder' ? defaultOpen : undefined),
          children: children,
        };
      });
    };
    setInternalFileTree(enrichTree(initialFileTree, internalFileTree));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFileTree]);

  const handleToggleFolder = useCallback((path: string) => {
    setInternalFileTree(prevTree => {
      const toggle = (nodes: FileSystemEntry[]): FileSystemEntry[] =>
        nodes.map(node => {
          if (node.path === path && node.type === 'folder') {
            return { ...node, isOpen: !node.isOpen };
          }
          if (node.children) {
            return { ...node, children: toggle(node.children) };
          }
          return node;
        });
      return toggle(prevTree);
    });
  }, []);

  const renderTree = (nodes: FileSystemEntry[], level: number): React.ReactNode => {
    return nodes.map(node => (
      <React.Fragment key={node.id}>
        <FileNode
          node={node}
          onFileSelect={onFileSelect}
          onToggleFolder={handleToggleFolder}
          activeFile={activeFile}
          level={level}
        />
        {node.type === 'folder' && node.isOpen && node.children && (
          <div className="folder-children">
            {renderTree(node.children, level + 1)}
          </div>
        )}
      </React.Fragment>
    ));
  };

  return (
    <div className="futuristic-file-explorer">
      <div className="explorer-header">Project Files</div>
      <div className="explorer-content">
        {internalFileTree.length > 0 ? (
          renderTree(internalFileTree, 0)
        ) : (
          <div className="no-files">No files in project.</div>
        )}
      </div>
    </div>
  );
};
export default FuturisticFileExplorer;
